<template>
    <component :is="tag">
        <slot
            :affiliate-links="affiliateLinks"
            :is-loading="isLoading"
            :handle-submit="handleSubmit"
            :can-create-deep-link="canCreateDeepLink"
        />
    </component>
</template>

<script>
    import BaseActivationBriefStepMixin from './mixins/BaseActivationBriefStepMixin';
    import useAffiliateLinks from '../../../src/composables/use-affiliate-links';
    import useMessage from '../../../src/composables/use-message';
    import {
        EVENT_CREATE_AFFILIATE_LINK,
        EVENT_NAME_DEEP_LINK_VALIDATION_ERROR
    } from '../../constants/analytics-events';
    import EventTrackingMixin from '../../mixins/EventTrackingMixin';

    export default {
        name: 'ActivationBriefAffiliateLinksProvider',
        mixins: [BaseActivationBriefStepMixin, EventTrackingMixin],
        data() {
            return {
                isLoading: false,
                isSubmitting: false,
                advancedAffiliatePackage: this.$root.advancedAffiliatePackage
            };
        },

        methods: {
            async handleSubmit(linkId, parentLinkId, url) {
                this.isLoading = true;
                const { message } = useMessage();
                const { createDeepLink } = useAffiliateLinks();
                try {
                    await createDeepLink({
                        linkId,
                        parentLinkId,
                        url,
                        contactId: this.$root.contact_id
                    });

                    await this.loadAffiliateLinks();
                    await this.fetchMissingLinksContext();
                    message.success('Your link has been created.');
                    this.trackLiveUrlEvent(EVENT_CREATE_AFFILIATE_LINK);
                } catch (error) {
                    if (error.status === 422) {
                        this.trackLiveUrlEvent(EVENT_NAME_DEEP_LINK_VALIDATION_ERROR, {
                            error: error.data,
                            was_successful: false,
                            url
                        });
                    }

                    message.responseError(error);
                } finally {
                    this.isLoading = false;
                }
            },
            canSelfServeAffiliateLinks() {
                const linkConversionConfig = this.brief.conversion_configs.find(
                    // eslint-disable-next-line camelcase
                    ({ brief_id, affiliate_group_id }) => brief_id === this.brief.id && affiliate_group_id !== null
                );
                return linkConversionConfig?.can_self_serve_links || false;
            },
            canCreateDeepLink() {
                return this.canSelfServeAffiliateLinks() && this.$root.advancedAffiliatePackage;
            },
            shouldRedirectToHome() {
                return this.affiliateLinks.length === 0;
            },
            async fetchMissingLinksContext() {
                const urlsToFetch = [];
                this.affiliateLinks?.forEach((link) => {
                    // If attempt_refresh does not exist or is true, add url to fetch list
                    if (link.context?.attempt_refresh !== false) {
                        urlsToFetch.push(link.original_url);
                    }
                });
                if (urlsToFetch.length === 0) {
                    return;
                }

                try {
                    const resp = await useAffiliateLinks().fetchLinksContext({
                        urls: urlsToFetch,
                        contactId: this.$root.contact_id
                    });
                    if (!resp.ok) {
                        return;
                    }
                    const linksContext = resp.data;

                    if (linksContext && typeof linksContext === 'object') {
                        linksContext.forEach((linkContext) => {
                            const index = this.affiliateLinks.findIndex(
                                (link) => link.original_url === linkContext.original_url
                            );
                            if (index === -1) {
                                return;
                            }
                            this.affiliateLinks[index].context = linkContext;
                        });
                    }
                } catch (error) {
                    // Silently fail if context cannot be fetched
                    console.error(error);
                }
            }
        },
        created() {
            this.isLoading = true;

            this.loadContact()
                .then(() => {
                    return Promise.all([this.loadActivationBrief(), this.loadAffiliateLinks()]);
                })
                .then(() => {
                    this.fetchMissingLinksContext();
                })
                .catch((error) => this.handleHttpError(error))
                .then(() => {
                    this.isLoading = false;
                });
        },
        watch: {
            isLoading: {
                // Once data is fetched, check if we should hide/redirect this page
                handler(newLoading, oldLoading) {
                    if (oldLoading === true && newLoading === false) {
                        if (this.shouldRedirectToHome()) {
                            this.$router.replace({ name: 'sponsorships' });
                        }
                    }
                }
            }
        }
    };
</script>

<style scoped lang="scss"></style>
